import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Warning, ArrowSquareOut } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

/**
 * Producer Disputes page.
 * Lists every Stripe chargeback opened against the producer's shows so they
 * can respond within the 7-day evidence window. Links out to Stripe dashboard
 * for actual evidence submission.
 */
const STATUS_COLORS = {
  needs_response: { bg: '#B91C1C', fg: '#FEF2F2', label: 'Needs Response' },
  warning_needs_response: { bg: '#D97706', fg: '#FEF3C7', label: 'Warning' },
  warning_under_review: { bg: '#D97706', fg: '#FEF3C7', label: 'Warning' },
  warning_closed: { bg: '#6B7280', fg: '#F3F4F6', label: 'Warning Closed' },
  under_review: { bg: '#D97706', fg: '#FEF3C7', label: 'Under Review' },
  won: { bg: '#059669', fg: '#D1FAE5', label: 'Won' },
  lost: { bg: '#4B5563', fg: '#F3F4F6', label: 'Lost' },
  charge_refunded: { bg: '#6B7280', fg: '#F3F4F6', label: 'Refunded' },
};

const ProducerDisputes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);

  const userRoles = useMemo(() => user?.roles || (user?.role ? [user.role] : []), [user]);
  const canAccess = userRoles.some((r) => ['ORG_ADMIN', 'MASTER', 'LMASTER'].includes(r));

  useEffect(() => {
    if (!canAccess) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get('/disputes');
        setDisputes(data.disputes || []);
      } catch {
        toast.error('Failed to load disputes');
      } finally {
        setLoading(false);
      }
    })();
  }, [canAccess]);

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="text-[#9E9E9E]" data-testid="disputes-denied">Producer access required</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#E5E5E5]" data-testid="disputes-page">
      <nav className="bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-[#9E9E9E] hover:text-white transition-colors text-sm"
              data-testid="disputes-back"
            >
              <ArrowLeft size={18} /> Back to Dashboard
            </button>
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>
              Chargebacks &amp; Disputes
            </h1>
            <div />
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start gap-3 mb-6 bg-[#B91C1C]/10 border border-[#B91C1C]/30 rounded-lg p-4">
          <Warning size={22} weight="duotone" className="text-[#FCA5A5] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-white font-semibold">
              Respond within the first 48 hours whenever possible
            </p>
            <p className="text-xs text-[#9E9E9E] mt-1">
              Stripe gives you ~7 days to submit evidence. Faster responses dramatically improve
              win rates. Click a dispute below to open it in your Stripe dashboard and upload
              supporting documents.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="shimmer h-24 rounded-lg" />
        ) : disputes.length === 0 ? (
          <div
            className="bg-[#111] border border-[#1F1F1F] rounded-lg p-10 text-center"
            data-testid="disputes-empty"
          >
            <div className="w-14 h-14 rounded-full bg-[#059669]/15 flex items-center justify-center mx-auto mb-3">
              <span className="text-[#34D399] text-2xl font-black">✓</span>
            </div>
            <p className="text-white font-semibold">No disputes — clean record</p>
            <p className="text-xs text-[#9E9E9E] mt-1">
              If a rider ever files a chargeback, you&apos;ll see it here within minutes of Stripe
              notifying us. An alert email will also be sent to the show&apos;s producer contact
              address.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {disputes.map((d) => {
              const status = STATUS_COLORS[d.status] || { bg: '#6B7280', fg: '#F3F4F6', label: d.status || 'Unknown' };
              return (
                <a
                  key={d.id}
                  href={d.stripe_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-[#111] border border-[#1F1F1F] hover:border-[#B91C1C]/40 rounded-lg p-4 transition-colors"
                  data-testid={`dispute-row-${d.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                          style={{ background: status.bg, color: status.fg }}
                        >
                          {status.label}
                        </span>
                        <span className="text-white font-bold text-base">
                          ${((d.amount_cents || 0) / 100).toFixed(2)}
                        </span>
                        <span className="text-[11px] text-[#6B6B6B]">
                          {(d.currency || 'USD').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-[#E5E5E5] mt-1">
                        {d.rider_name || 'Unknown rider'}
                        {d.rider_email && (
                          <span className="text-[#6B6B6B]"> · {d.rider_email}</span>
                        )}
                      </p>
                      <p className="text-[11px] text-[#6B6B6B] mt-0.5">
                        Reason: {String(d.reason || 'unknown').replace(/_/g, ' ')}
                        {d.created_at && ` · Opened ${new Date(d.created_at).toLocaleDateString()}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-[#635BFF] text-xs font-semibold">
                      Respond in Stripe <ArrowSquareOut size={14} weight="bold" />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProducerDisputes;
